import { type FC, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { Header } from 'dibs-elements/exports/Header';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { FormattedNumber } from 'dibs-react-intl';
import {
    categoryLabel,
    mediumLabel,
    materialLabel,
    recentSalesTitle,
    recentSalesDescription,
    getRecentSalesHeaders,
} from '../SbSharedRecentSalesMessages/SbSharedRecentSalesMessages';
import { SbSharedRecentSalesOverview } from '../SbSharedRecentSalesOverview/SbSharedRecentSalesOverview';
import { VERTICALS } from '../../constants/uriConstants';
import { FILTER_NAME_CREATOR } from '../../constants/sbConstants';
import { getAppliedFilterDisplayName } from '../helpers/appliedFilters/getAppliedFilterDisplayName';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbRespRecentSales.module.css';

import { type SbRespRecentSales_creatorRootPage$key } from './__generated__/SbRespRecentSales_creatorRootPage.graphql';
import { type SbRespRecentSales_itemSearch$key } from './__generated__/SbRespRecentSales_itemSearch.graphql';

const creatorRootPageFragment = graphql`
    fragment SbRespRecentSales_creatorRootPage on CreatorRootPage {
        verticals
        CreatorRootTransactions {
            ...SbSharedRecentSalesOverview_creatorRootTransactions
            recentSales {
                soldDate
                soldPrice
                categories
                medium
                creationDate
                period
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment SbRespRecentSales_itemSearch on ItemSearchQueryConnection {
        ...getAppliedFilterDisplayName_itemSearch
    }
`;

type Props = {
    creatorRootPage: SbRespRecentSales_creatorRootPage$key;
    itemSearch: SbRespRecentSales_itemSearch$key;
    currency: string;
};

export const SbRespRecentSales: FC<Props> = ({
    creatorRootPage: creatorRootPageRef,
    itemSearch: itemSearchRef,
    currency,
}) => {
    const creatorRootPage = useFragment(creatorRootPageFragment, creatorRootPageRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);

    const [showTooltip, setShowTooltip] = useState(false);
    const creatorRootTransactions = creatorRootPage?.CreatorRootTransactions;
    const recentSales = creatorRootTransactions?.recentSales || [];
    const topVertical = creatorRootPage?.verticals?.[0];
    const madeOfLabel = topVertical === VERTICALS.ART ? mediumLabel : materialLabel;
    const headerLabels = getRecentSalesHeaders({ topVertical });

    const creatorName = getAppliedFilterDisplayName(itemSearch, FILTER_NAME_CREATOR) || '';

    const rowClass = classnames(dibsCss.flex, dibsCss.pyXsmall, dibsCss.pxLarge);

    return recentSales.length > 0 ? (
        <div className={classnames(dibsCss.flex, dibsCss.pMedium, dibsCss.flexCol)}>
            <HeadingLevel>
                {Heading => (
                    <div className={classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.mbSmall)}>
                        <Header title={recentSalesTitle(creatorName)} htmlElementType={Heading} />
                        <div
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                            className={dibsCss.relative}
                        >
                            <div
                                className={classnames(
                                    dibsCss.flex,
                                    dibsCss.cursorPointer,
                                    dibsCss.mlXsmall
                                )}
                            >
                                <InfoCircle className={classnames(dibsCss.w18px, dibsCss.h18px)} />
                            </div>
                            <Tooltip isVisible={showTooltip} direction="right">
                                <div
                                    className={dibsCss.whitespaceNormal}
                                    id="sb.SbRespRecentSales.tooltip"
                                >
                                    {recentSalesDescription}
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                )}
            </HeadingLevel>

            <div
                className={classnames(
                    styles.tableContainer,
                    dibsCss.flex,
                    dibsCss.border,
                    dibsCss.borderSolid,
                    dibsCss.borderMoonstone
                )}
            >
                <div className={classnames(styles.tableLeftCell, dibsCss.alignTop)}>
                    <table className={classnames(styles.mainTable, dibsCss.wFull)}>
                        <thead>
                            <tr
                                className={classnames(
                                    styles.headerRow,
                                    rowClass,
                                    dibsCss.bgMoonstone,
                                    dibsCss.textStart
                                )}
                            >
                                {headerLabels.map((label, idx) => (
                                    <td
                                        key={`recent-sales-header-${idx}`}
                                        className={classnames(
                                            dibsCss.whitespaceNowrap,
                                            dibsCss.sassyFontSubHeaderSizeSmall,
                                            [categoryLabel, madeOfLabel].includes(label)
                                                ? styles.headerLabelLarge
                                                : styles.headerLabel
                                        )}
                                    >
                                        {label}
                                    </td>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <div
                                        className={classnames(
                                            styles.mainTableContainer,
                                            dibsCss.overflowYAuto,
                                            dibsCss.sassyFontBodySizeSmall
                                        )}
                                    >
                                        <table
                                            className={classnames(styles.mainTable, dibsCss.wFull)}
                                        >
                                            <tbody>
                                                {recentSales
                                                    .filter(filterFalsy)
                                                    .map(
                                                        (
                                                            {
                                                                soldDate,
                                                                soldPrice,
                                                                categories,
                                                                medium,
                                                                creationDate,
                                                                period,
                                                            },
                                                            idx
                                                        ) => (
                                                            <tr
                                                                className={rowClass}
                                                                key={`resp-recent-sales-heading-${idx}`}
                                                            >
                                                                <td
                                                                    className={
                                                                        styles.columnsContainer
                                                                    }
                                                                >
                                                                    {soldDate}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        styles.columnsContainer
                                                                    }
                                                                >
                                                                    {soldPrice && (
                                                                        <FormattedNumber
                                                                            value={soldPrice}
                                                                            style="currency"
                                                                            currency={currency}
                                                                            maximumFractionDigits={
                                                                                0
                                                                            }
                                                                            minimumFractionDigits={
                                                                                0
                                                                            }
                                                                        />
                                                                    )}
                                                                </td>
                                                                <td
                                                                    className={
                                                                        styles.columnsLargeContainer
                                                                    }
                                                                >
                                                                    {(categories || []).join(', ')}
                                                                </td>
                                                                {topVertical ===
                                                                VERTICALS.FASHION ? (
                                                                    <td
                                                                        className={
                                                                            styles.columnsContainer
                                                                        }
                                                                    >
                                                                        {period}
                                                                    </td>
                                                                ) : (
                                                                    <>
                                                                        <td
                                                                            className={
                                                                                styles.columnsLargeContainer
                                                                            }
                                                                        >
                                                                            {(medium || []).join(
                                                                                ', '
                                                                            )}
                                                                        </td>
                                                                        <td
                                                                            className={
                                                                                styles.columnsContainer
                                                                            }
                                                                        >
                                                                            {creationDate}
                                                                        </td>
                                                                    </>
                                                                )}
                                                            </tr>
                                                        )
                                                    )}
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div
                    className={classnames(
                        styles.tableRightCell,
                        dibsCss.overflowYScroll,
                        dibsCss.sassyFontBody,
                        dibsCss.pxLarger,
                        dibsCss.borderL,
                        dibsCss.borderLSolid,
                        dibsCss.borderLMoonstone
                    )}
                >
                    <SbSharedRecentSalesOverview
                        currency={currency}
                        creatorRootTransactions={creatorRootTransactions}
                    />
                </div>
            </div>
        </div>
    ) : null;
};
